<script setup>
/**
 * FormControlEmailMessageHtmlBody
 * Provides a tabbed interface for Email HTML:
 * 1. Preview: An iframe rendering the HTML content.
 * 2. HTML: A code editor for direct manipulation.
 */
import { ref, computed, onMounted, nextTick, getCurrentInstance } from 'vue';
import FormControlTypeCodeEditor from '../form-control-type/FormControlTypeCodeEditor.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: String,
        default: ''
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const activeTab = ref('preview');
const iframeRef = ref(null);

// --- Computed ---

const iframeHtml = computed(() => props.currentData[props.field.name] || '');

// --- Methods ---

/**
 * Adjusts the iframe height to match its internal content.
 * Called on iframe load to prevent double scrollbars.
 */
const updateIframeHeight = () => {
    const iframe = iframeRef.value;
    if (iframe && iframe.contentWindow) {
        // Reset height first to get accurate scrollHeight
        iframe.style.height = '250px';
        const newHeight = iframe.contentWindow.document.body.offsetHeight;
        iframe.style.height = (newHeight + 20) + 'px';
    }
};

/**
 * Handles tab switching.
 * Using nextTick ensures that if we switch to 'preview',
 * the iframe exists before we try to attach listeners.
 */
const setTab = async (tab) => {
    activeTab.value = tab;
    if (tab === 'preview') {
        await nextTick();
        // The onload event in the template handles the initial sizing
    }
};

// --- Lifecycle ---

onMounted(() => {
    // Hide the required label to keep the tab layout clean
    props.field.required = false;
});
</script>

<template>
    <div>
        <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item" role="presentation">
                <a
                    class="nav-link"
                    :class="{ active: activeTab === 'preview' }"
                    href="#"
                    @click.prevent="setTab('preview')"
                >
                    Preview
                </a>
            </li>
            <li class="nav-item" role="presentation">
                <a
                    class="nav-link"
                    :class="{ active: activeTab === 'html' }"
                    href="#"
                    @click.prevent="setTab('html')"
                >
                    HTML
                </a>
            </li>
        </ul>

        <div class="tab-content">
            <div
                v-if="activeTab === 'preview'"
                class="tab-pane active"
                role="tabpanel"
            >
                <iframe
                    ref="iframeRef"
                    :srcdoc="iframeHtml"
                    @load="updateIframeHeight"
                    style="width: 100%; min-height: 250px; border: solid #ddd; border-width: 0 1px 1px 1px"
                />
            </div>

            <div
                v-if="activeTab === 'html'"
                class="tab-pane active"
                role="tabpanel"
            >
                <FormControlTypeCodeEditor
                    v-bind="$props"
                    v-on="$listeners"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Standard Bootstrap tab-pane behavior usually requires 'active' class to show */
.tab-pane {
    display: none;
}
.tab-pane.active {
    display: block;
}
</style>